import React, { useEffect, useState, useRef } from "react";
import api from "../api";
import { format } from "date-fns";
import AttendanceCalendarWithEdit from "../components/AttendanceCalendarWithEdit";
import {
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, TextField, Select, MenuItem, Button,
  InputLabel, FormControl
} from "@mui/material";

export default function ManageAttendance() {
  const calendarRef = useRef(null);
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [attendanceData, setAttendanceData] = useState([]);
  const [newDate, setNewDate] = useState("");
  const [newStatus, setNewStatus] = useState("Present");

  useEffect(() => {
    api.get(`${process.env.REACT_APP_API_BASE_URL}students/approved`).then((res) => {
      setStudents(res.data);
      setFilteredStudents(res.data);
    });
  }, []);

  useEffect(() => {
    if (selectedStudentId) fetchAttendance();
  }, [selectedStudentId]);

  const fetchAttendance = async () => {
    const student = students.find((s) => s._id === selectedStudentId);
    if (!student?.email) return;
    const res = await api.get(`${process.env.REACT_APP_API_BASE_URL}attendance/student/${student.email}`);
    setAttendanceData(res.data);
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearch(term);
    setFilteredStudents(
      students.filter((s) =>
        `${s.name} ${s.email}`.toLowerCase().includes(term)
      )
    );
  };

  const handleAddAttendance = async () => {
    if (!selectedStudentId || !newDate) return;
    const email = students.find((s) => s._id === selectedStudentId)?.email;
    try {
      await api.post(`${process.env.REACT_APP_API_BASE_URL}attendance/manual-add`, {
        email,
        date: newDate,
        status: newStatus,
      });
      setNewDate("");
      setNewStatus("Present");
      fetchAttendance();
    } catch (err) {
      console.error("Error adding/updating attendance", err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      await api.delete(`${process.env.REACT_APP_API_BASE_URL}attendance/delete/${id}`);
      fetchAttendance();
    } catch (err) {
      console.error("Error deleting", err);
    }
  };

  const handleEdit = async (id, status) => {
    try {
      await api.put(`${process.env.REACT_APP_API_BASE_URL}attendance/update-status/${id}`, { status });
      fetchAttendance();
    } catch (err) {
      console.error("Error updating", err);
    }
  };

  return (
    <div className="p-6 space-y-6 bg-white/10 rounded-2xl shadow-md backdrop-blur-md border border-white/20">
      <h2 className="text-2xl font-bold text-gray-800">Manage Attendance</h2>

      <TextField
        fullWidth
        label="Search Student by Name or Email"
        value={search}
        onChange={handleSearch}
        variant="outlined"
        className="bg-white/60 backdrop-blur-sm rounded"
      />

<TableContainer
  component={Paper}
  sx={{
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    backdropFilter: "blur(10px)",
    borderRadius: 2,
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
    border: "1px solid rgba(255,255,255,0.1)"
  }}
>
        <Table>
          <TableHead className="bg-white/5 backdrop-blur-lg rounded-2xl p-4 shadow-lg border border-white/10">
            <TableRow>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>College</strong></TableCell>
              <TableCell><strong>Action</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredStudents.map((s) => (
              <TableRow
                key={s._id}
                selected={selectedStudentId === s._id}
                className="hover:bg-white/10 transition"
              >
                <TableCell>{s.name}</TableCell>
                <TableCell>{s.email}</TableCell>
                <TableCell>{s.college?.name}</TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => {
                      setSelectedStudentId(s._id);
                      setTimeout(() => {
                        calendarRef.current?.scrollIntoView({ behavior: "smooth" });
                      }, 100); // small delay ensures calendar is rendered
                    }}
                  >
                    View Attendance
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {selectedStudentId && (
        <>
          <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-4 shadow-lg border border-white/10">
            <h3 className="text-lg font-semibold mb-2 text-gray-800">Add / Update Attendance</h3>
            <div className="flex gap-4 flex-wrap">
              <TextField
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                label="Date"
                InputLabelProps={{ shrink: true }}
                className="bg-white/70 backdrop-blur-sm rounded"
              />
              <FormControl>
                <InputLabel>Status</InputLabel>
                <Select
                  value={newStatus}
                  label="Status"
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="bg-white/70 backdrop-blur-sm rounded"
                >
                  <MenuItem value="Present">Present</MenuItem>
                  <MenuItem value="Absent">Absent</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                color="success"
                onClick={handleAddAttendance}
              >
                Add / Update
              </Button>
            </div>
          </div>

          <div
            ref={calendarRef}
            className="mt-6 border border-white/30 bg-white/20 rounded-xl p-4 shadow backdrop-blur-md"
          >
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Attendance Calendar View</h3>
            <AttendanceCalendarWithEdit
              studentEmail={students.find((s) => s._id === selectedStudentId)?.email}
            />
          </div>
        </>
      )}
    </div>
  );
}